/* eslint-disable no-console */
//
// Cydea shared console logger.
// -----------------------------------------------------------------------------
// One styled, domain-tagged, severity-aware console logger per app area, all
// exported from this single file and all driven by the `loggerConfig` object
// below. The goals:
//
//   • Humans get scannable, colour-coded console output while developing and
//     debugging features (each domain has its own badge colour).
//   • AI assistants get clean, greppable plain-text prefixes ("cydea › gql ›
//     debug › …") when a developer pastes console output into a chat.
//   • Production is safe by default: every logger is silenced in production
//     builds (`loggerConfig.production.enabled === false`). Even if a developer
//     force-enables prod logging, only messages at/above `production.minLevel`
//     are allowed through — so verbose/debug output can never ship to users.
//
// This is intentionally NOT an error-reporting tool — genuine errors go to
// Sentry (see src/sentry/scrub.ts and the capture sites across the app). This
// is a developer/AI debugging aid that prints to the browser console only.
//
// Usage:
//   import { logger } from 'src/utils/logger';
//   logger.gql.debug('operation start', operationName, variables);
//   logger.auth.warn('token refresh fell back to interactive login');
//
// To quieten a noisy domain while debugging something else, flip its `enabled`
// flag (or raise its `level`) in `loggerConfig.domains` below — no other code
// changes required.

import { env } from 'src/config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// The set of app domains. Pick the one that best matches where the log
// originates; use `app` for anything that does not fit a specific subsystem.
export type LogDomain =
  | 'auth' // authentication, tokens, session lifecycle
  | 'gql' // GraphQL / Apollo client
  | 'rest' // REST / axios client
  | 'realtime' // websockets / Reverb / live progress
  | 'export' // export & report generation
  | 'chart' // chart rendering & image capture
  | 'data' // data parsing / transforms (CSV, JSON, formulas)
  | 'app'; // generic / cross-cutting / UI

interface DomainConfig {
  /** When false the domain prints nothing in any environment. */
  enabled: boolean;
  /** Minimum level this domain prints (in development). */
  level: LogLevel;
  /** Badge colour (any CSS colour). */
  color: string;
}

interface LoggerConfig {
  production: {
    /**
     * Master production switch. MUST stay false for normal releases — this is
     * the guarantee that debug/info logging never reaches end users. Flip to
     * true only for a deliberate production diagnostic session.
     */
    enabled: boolean;
    /** When prod logging is enabled, only this level and above may print. */
    minLevel: LogLevel;
  };
  domains: Record<LogDomain, DomainConfig>;
}

// -----------------------------------------------------------------------------
// Configuration — the single place to turn loggers on/off and tune verbosity.
// -----------------------------------------------------------------------------
export const loggerConfig: LoggerConfig = {
  production: {
    enabled: false,
    minLevel: 'error',
  },
  domains: {
    auth: { enabled: true, level: 'debug', color: '#7c3aed' },
    gql: { enabled: true, level: 'debug', color: '#e10098' },
    rest: { enabled: true, level: 'debug', color: '#0ea5e9' },
    realtime: { enabled: true, level: 'debug', color: '#10b981' },
    export: { enabled: true, level: 'debug', color: '#f59e0b' },
    chart: { enabled: true, level: 'debug', color: '#6366f1' },
    data: { enabled: true, level: 'debug', color: '#14b8a6' },
    app: { enabled: true, level: 'debug', color: '#64748b' },
  },
};

// Only the literal production environment is hard-gated. test/staging behave
// like development so deployed-but-internal debugging still works; add 'staging'
// or 'test' here if you ever need to silence those too.
const isProductionEnv = env === 'production';

const LEVEL_RANK: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

const CONSOLE_METHOD: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
};

const shouldLog = (domain: LogDomain, level: LogLevel): boolean => {
  const domainCfg = loggerConfig.domains[domain];
  if (!domainCfg || !domainCfg.enabled) return false;
  if (LEVEL_RANK[level] < LEVEL_RANK[domainCfg.level]) return false;

  if (isProductionEnv) {
    if (!loggerConfig.production.enabled) return false;
    if (LEVEL_RANK[level] < LEVEL_RANK[loggerConfig.production.minLevel]) return false;
  }
  return true;
};

const BRAND_CSS = 'background:#022033;color:#fff;border-radius:3px 0 0 3px;padding:1px 5px;font-weight:600';
const RESET_CSS = 'color:inherit;background:transparent;font-weight:400';

const emit = (domain: LogDomain, level: LogLevel, args: unknown[]): void => {
  if (!shouldLog(domain, level)) return;

  const domainCss = `background:${loggerConfig.domains[domain].color};color:#fff;border-radius:0 3px 3px 0;padding:1px 5px;font-weight:600`;
  // The label reads "cydea › <domain> › <level>" so it stays meaningful when the
  // %c styling is stripped (e.g. copied as plain text into an AI chat).
  const label = `%c cydea %c ${domain} › ${level} %c`;
  console[CONSOLE_METHOD[level]](label, BRAND_CSS, domainCss, RESET_CSS, ...args);
};

export interface DomainLogger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

const makeLogger = (domain: LogDomain): DomainLogger => ({
  debug: (...args) => emit(domain, 'debug', args),
  info: (...args) => emit(domain, 'info', args),
  warn: (...args) => emit(domain, 'warn', args),
  error: (...args) => emit(domain, 'error', args),
});

// -----------------------------------------------------------------------------
// The loggers. `logger.<domain>.<level>(...)` is the primary API; individual
// domain loggers are also exported for ergonomic named imports.
// -----------------------------------------------------------------------------
export const authLog = makeLogger('auth');
export const gqlLog = makeLogger('gql');
export const restLog = makeLogger('rest');
export const realtimeLog = makeLogger('realtime');
export const exportLog = makeLogger('export');
export const chartLog = makeLogger('chart');
export const dataLog = makeLogger('data');
export const appLog = makeLogger('app');

export const logger: Record<LogDomain, DomainLogger> = {
  auth: authLog,
  gql: gqlLog,
  rest: restLog,
  realtime: realtimeLog,
  export: exportLog,
  chart: chartLog,
  data: dataLog,
  app: appLog,
};
